import { useEffect, useMemo, useRef, useState } from 'react';
import { toWesternDigits } from '@domain';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';

/**
 * A list you can type into.
 *
 * A plain dropdown is fine for three choices and unusable for three hundred.
 * A practice with a few years of history has hundreds of contacts and a chart
 * of accounts to match, and scrolling to find one is not a thing anybody should
 * be asked to do.
 *
 * Typing filters; the keyboard works throughout; and where a thing can be
 * created, the offer to create it sits at the bottom of the filtered list with
 * what you typed already in it — because searching for something and not
 * finding it is exactly the moment you want to add it.
 */

export interface SelectOption {
  value: string;
  label: string;
  /** Optional heading, used to group the list the way Xero groups accounts. */
  group?: string;
}

/**
 * What a search matches on.
 *
 * Arabic-Indic digits are folded to Western, so typing ٤٠٣٠ finds account 4030
 * — the same normalisation the rest of the product applies to typed figures.
 * Somebody typing an account code on an Arabic keyboard should not have to
 * think about which digits they are using.
 */
function searchable(text: string): string {
  return toWesternDigits(text).toLowerCase();
}

export function SearchableSelect({
  value,
  options,
  placeholder,
  emptyLabel,
  disabled = false,
  invalid = false,
  createLabel,
  onCreate,
  onChange,
  t,
  style,
}: {
  value: string;
  options: SelectOption[];
  placeholder: string;
  /** Shown when there is nothing at all to choose from. */
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  createLabel?: string;
  onCreate?: (typed: string) => void;
  onChange: (value: string) => void;
  t: Translate;
  style?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value) ?? null;

  const matches = useMemo(() => {
    const needle = searchable(query.trim());
    if (!needle) return options;
    return options.filter((option) => searchable(`${option.label} ${option.group ?? ''}`).includes(needle));
  }, [options, query]);

  // The create row is one past the end of the matches, so one index covers both.
  const createIndex = onCreate ? matches.length : -1;
  const lastIndex = onCreate ? matches.length : matches.length - 1;

  useEffect(() => {
    if (!open) return;
    const onDocumentDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentDown);
    return () => document.removeEventListener('mousedown', onDocumentDown);
  }, [open]);

  const choose = (option: SelectOption) => {
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  const create = () => {
    onCreate?.(query.trim());
    setQuery('');
    setOpen(false);
  };

  const commitHighlighted = () => {
    if (highlight === createIndex) return create();
    const option = matches[highlight];
    if (option) choose(option);
  };

  const control: React.CSSProperties = {
    height: '34px',
    width: '100%',
    border: `1px solid ${invalid ? theme.bad : theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: disabled ? theme.surfaceSunken : '#fff',
    color: disabled ? theme.textMuted : theme.text,
    ...style,
  };

  if (disabled) {
    return <input readOnly value={emptyLabel ?? ''} style={control} />;
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={open ? query : (selected?.label ?? '')}
        placeholder={selected ? selected.label : placeholder}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlight(0);
          setOpen(true);
        }}
        onFocus={() => {
          setQuery('');
          setHighlight(0);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(h + 1, lastIndex));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (event.key === 'Enter') {
            if (!open) return;
            event.preventDefault();
            commitHighlighted();
          } else if (event.key === 'Escape') {
            setOpen(false);
            setQuery('');
          }
        }}
        style={control}
      />

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            top: 'calc(100% + 3px)',
            zIndex: 40,
            background: '#fff',
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            boxShadow: theme.shadowCard,
            maxHeight: '260px',
            overflowY: 'auto',
          }}
        >
          {matches.length === 0 && !onCreate && (
            <div style={{ padding: '10px 12px', fontSize: '12.5px', color: theme.textMuted }}>
              {t('Nothing matches that.')}
            </div>
          )}

          {matches.map((option, index) => {
            const previous = matches[index - 1];
            const newGroup = option.group && option.group !== previous?.group;
            return (
              <div key={option.value}>
                {newGroup && (
                  <div
                    style={{
                      padding: '7px 12px 4px',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      letterSpacing: '.07em',
                      textTransform: 'uppercase',
                      color: theme.textSubtle,
                    }}
                  >
                    {option.group}
                  </div>
                )}
                <div
                  role="option"
                  aria-selected={option.value === value}
                  onMouseEnter={() => setHighlight(index)}
                  onMouseDown={(event) => {
                    // mousedown, not click: the input's blur would close the
                    // list before a click ever landed.
                    event.preventDefault();
                    choose(option);
                  }}
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    background: index === highlight ? theme.surfaceSunken : 'transparent',
                    fontWeight: option.value === value ? 600 : 400,
                  }}
                >
                  {option.label}
                </div>
              </div>
            );
          })}

          {onCreate && createLabel && (
            <div
              role="option"
              aria-selected={false}
              onMouseEnter={() => setHighlight(createIndex)}
              onMouseDown={(event) => {
                event.preventDefault();
                create();
              }}
              style={{
                padding: '9px 12px',
                fontSize: '12.5px',
                fontWeight: 600,
                color: theme.ink,
                cursor: 'pointer',
                borderTop: `1px solid ${theme.divider}`,
                background: highlight === createIndex ? theme.surfaceSunken : 'transparent',
              }}
            >
              {createLabel}
              {query.trim() && (
                <span style={{ color: theme.textMuted, fontWeight: 400 }}> — “{query.trim()}”</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
